// ABOUTME: Send invoice dialog with email preview and optional CC recipients
import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button,
    Box, Typography, CircularProgress, Alert, Divider, Chip,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import invoicingService from '../../services/invoicing';

const SendDialog = ({ open, invoiceId, onClose, onSuccess }) => {
    const [preview, setPreview] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [toEmail, setToEmail] = useState('');
    const [ccInput, setCcInput] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open && invoiceId) {
            fetchPreview();
        }
        return () => { setPreview(null); setToEmail(''); setCcInput(''); setError(null); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, invoiceId]);

    const fetchPreview = async () => {
        try {
            setLoadingPreview(true);
            const res = await invoicingService.previewEmail(invoiceId);
            setPreview(res.data);
            setToEmail(res.data.recipient || '');
        } catch (err) {
            setError('Could not load email preview');
        } finally {
            setLoadingPreview(false);
        }
    };

    const parsedCcEmails = ccInput
        .split(',')
        .map(e => e.trim())
        .filter(e => e.length > 0);

    const toEmailMissing = !toEmail.trim();

    const handleSend = async () => {
        try {
            setSending(true);
            setError(null);
            const payload = { cc_emails: parsedCcEmails };
            if (!preview?.recipient) payload.to_email = toEmail.trim();
            await invoicingService.sendInvoice(invoiceId, payload);
            toast.success('Invoice sent successfully');
            onClose();
            await onSuccess();
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to send invoice';
            setError(msg);
            toast.error(msg);
        } finally {
            setSending(false);
        }
    };

    // Show progress overlay while sending (like report generation)
    if (sending) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999,
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: 'white',
                            borderRadius: 4,
                            padding: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                        }}
                    >
                        <CircularProgress size={60} />
                        <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>
                            Sending Invoice...
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                            Generating PDF, uploading to server, and sending email.
                            <br />
                            This may take a few moments.
                        </Typography>
                    </Box>
                </Box>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Send Invoice</DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {loadingPreview ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : preview && (
                    <>
                        <Box sx={{ mb: 2 }}>
                            {preview.recipient ? (
                                <>
                                    <Typography variant="caption" color="textSecondary">To</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{preview.recipient}</Typography>
                                </>
                            ) : (
                                <TextField
                                    label="To (recipient email)"
                                    value={toEmail}
                                    onChange={(e) => setToEmail(e.target.value)}
                                    placeholder="Enter recipient email"
                                    fullWidth
                                    size="small"
                                    required
                                    error={toEmailMissing}
                                    helperText={toEmailMissing ? 'Recipient email is required' : 'No email found on service order — enter manually'}
                                />
                            )}
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="textSecondary">Subject</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{preview.subject}</Typography>
                        </Box>

                        <TextField
                            label="CC (comma-separated emails)"
                            value={ccInput}
                            onChange={(e) => setCcInput(e.target.value)}
                            placeholder="e.g. accounts@company.com, manager@company.com"
                            fullWidth
                            size="small"
                            sx={{ mb: 2 }}
                            helperText="Optional — leave blank to send to primary contact only"
                        />

                        {parsedCcEmails.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                                {parsedCcEmails.map(e => <Chip key={e} label={e} size="small" />)}
                            </Box>
                        )}

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                            Email Preview
                        </Typography>
                        <Box
                            sx={{
                                border: '1px solid #e0e0e0', borderRadius: 1, p: 2,
                                maxHeight: 300, overflowY: 'auto', backgroundColor: '#fafafa',
                                fontSize: 13,
                            }}
                            dangerouslySetInnerHTML={{ __html: preview.body_html }}
                        />
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={sending}>Cancel</Button>
                <Button
                    onClick={handleSend}
                    variant="contained"
                    disabled={sending || loadingPreview || !preview || toEmailMissing}
                >
                    Confirm & Send
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SendDialog;
